import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Button,
  CircularProgress,
  Divider,
  Skeleton,
} from '@mui/material';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import EggIcon from '@mui/icons-material/Egg';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import { WidgetContainer } from '../../widgets.styles';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../../../../context/auth.context';
import { nutritionService } from '../../../../../../services/service-proxy';

/**
 * Circular progress indicator with centered value.
 */
const StatCircle = ({ value, max, color, icon, label }) => {
  const percentage = max > 0 ? Math.min((value / max) * 100, 100) : 0;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
      <Box sx={{ position: 'relative', display: 'inline-flex' }}>
        {/* Background circle */}
        <CircularProgress
          variant="determinate"
          value={100}
          size={56}
          thickness={4}
          sx={{ color: 'action.hover', position: 'absolute' }}
        />
        {/* Value circle */}
        <CircularProgress
          variant="determinate"
          value={percentage}
          size={56}
          thickness={4}
          sx={{ color }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            bottom: 0,
            right: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {React.cloneElement(icon, { sx: { fontSize: 20, color } })}
        </Box>
      </Box>
      <Typography variant="body2" sx={{ fontWeight: 700, mt: 1 }}>
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
  );
};

/**
 * Today's Stats widget - shows daily nutrition summary from live data.
 * Displays calories consumed, protein intake, and meal count.
 */
const TodayStats = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [goals, setGoals] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.uid) {
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        const [totals, userGoals] = await Promise.all([
          nutritionService.getDailyTotals(user.uid, new Date()),
          nutritionService.getNutritionGoals(user.uid),
        ]);
        setStats(totals);
        setGoals(userGoals);
      } catch (err) {
        if (process.env.NODE_ENV === 'development') {
          console.error('[TodayStats] Error fetching nutrition data:', err);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user?.uid]);

  const defaultGoals = goals || {
    dailyCalories: 2200,
    macroTargets: { protein: 150, carbs: 250, fat: 70, fiber: 30 },
  };

  const displayStats = stats || {
    calories: 0,
    protein: 0,
    carbs: 0,
    fat: 0,
    mealCount: 0,
  };

  return (
    <WidgetContainer>
      <Typography sx={{ p: 2.5, pb: 1 }} variant="h6" color="text.primary">
        Today&apos;s Stats
      </Typography>
      <Typography sx={{ px: 2.5, pb: 2 }} variant="caption" color="text.secondary">
        {loading ? 'Loading...' : 'Keep pushing towards your goals'}
      </Typography>
      <Divider />
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'space-around', py: 2.5, px: 1 }}>
          <Skeleton variant="circular" width={56} height={56} />
          <Skeleton variant="circular" width={56} height={56} />
          <Skeleton variant="circular" width={56} height={56} />
        </Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-around',
            py: 2.5,
            px: 1,
          }}
        >
          <StatCircle
            value={displayStats.calories}
            max={defaultGoals.dailyCalories}
            color="#F97316"
            icon={<LocalFireDepartmentIcon />}
            label="cal eaten"
          />
          <StatCircle
            value={displayStats.protein}
            max={defaultGoals.macroTargets.protein}
            color="#3B82F6"
            icon={<EggIcon />}
            label="g protein"
          />
          <StatCircle
            value={displayStats.mealCount}
            max={6}
            color="#22C55E"
            icon={<RestaurantIcon />}
            label="meals"
          />
        </Box>
      )}
      {!loading && displayStats.mealCount === 0 && (
        <>
          <Divider />
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}>
            <Button
              size="small"
              variant="text"
              color="primary"
              onClick={() => navigate('/nutrition')}
            >
              Log a meal
            </Button>
          </Box>
        </>
      )}
    </WidgetContainer>
  );
};

export default TodayStats;
